import { useState } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Loader2, CheckCircle2, Users } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';
import { getErrorMessage } from '../../lib/errors';

export function PennylaneBankfeedCounterpartyBackfillPanel() {
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [applying, setApplying] = useState(false);
  const [applyResult, setApplyResult] = useState(null);

  const runPreview = async () => {
    setLoading(true);
    setPreview(null);
    setApplyResult(null);
    try {
      const { data } = await api.get('/admin/diagnostics/pennylane-bankfeed-counterparty-backfill/preview');
      setPreview(data);
      if (data.proposed_count === 0) {
        toast.success('No transactions need a counterparty backfill');
      } else {
        toast.info(`${data.proposed_count} transaction(s) would get a counterparty`);
      }
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const runApply = async () => {
    if (!preview || preview.proposed_count === 0) return;
    if (!window.confirm(`Write counterparty on ${preview.proposed_count} transaction(s)?`)) return;
    setApplying(true);
    setApplyResult(null);
    try {
      const { data } = await api.post('/admin/diagnostics/pennylane-bankfeed-counterparty-backfill/apply');
      setApplyResult(data);
      setPreview(null);
      toast.success(`Backfilled ${data.written} transaction(s)`);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setApplying(false);
    }
  };

  return (
    <Card data-testid="pennylane-bankfeed-counterparty-backfill-panel">
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <Users className="h-5 w-5" />
          Pennylane Bank Feed Counterparty Backfill
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-muted-foreground">
          Re-derives counterparty on BCP transactions synced before the SEPA-label
          parser existed, from the description already stored on each document
          (NBE:/NPY: tags, or an untagged "VIR SEPA &lt;name&gt;" line). No live
          Pennylane call. Leaves bare fee/subscription descriptors null -- they
          genuinely carry no name.
        </p>

        <Button size="sm" onClick={runPreview} disabled={loading} data-testid="cp-backfill-preview">
          {loading ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
          Preview
        </Button>

        {preview && (
          <div className="space-y-3">
            <p className="text-sm">
              {preview.total_missing_counterparty} missing counterparty &middot;{' '}
              <span className="font-medium text-green-600">{preview.proposed_count} derivable</span> &middot;{' '}
              {preview.genuinely_absent_count} genuinely absent
            </p>
            {preview.proposed_count > 0 && (
              <Button size="sm" variant="default" onClick={runApply} disabled={applying} data-testid="cp-backfill-apply">
                {applying ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
                Apply {preview.proposed_count} correction(s)
              </Button>
            )}
          </div>
        )}

        {applyResult && (
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4 text-green-600" />
              <span className="text-sm font-medium">
                Written: {applyResult.written} &middot; Conflicts skipped: {applyResult.skipped_conflict}
              </span>
            </div>
            <p className="text-xs text-muted-foreground">Run: {applyResult.run_id}</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
